import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Smartphone, Brain, Users, CheckCircle, Sparkles, Shield, Target, Lightbulb } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const SOFT_SKILLS = [
  { name: "Orientamento all'altro", icon: Users, color: "bg-blue-50 text-blue-600" },
  { name: "Leadership", icon: Shield, color: "bg-rose-50 text-rose-600" },
  { name: "Resilienza", icon: Sparkles, color: "bg-amber-50 text-amber-600" },
  { name: "Organizzazione e Pianificazione", icon: CheckCircle, color: "bg-emerald-50 text-emerald-600" },
  { name: "Flessibilità cognitiva", icon: Lightbulb, color: "bg-purple-50 text-purple-600" },
  { name: "Orientamento agli obiettivi", icon: Target, color: "bg-cyan-50 text-cyan-600" },
];

export default function SolutionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="soluzione"
      data-testid="solution-section"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <Badge
            data-testid="solution-badge"
            className="mb-4 bg-[#4B4DF7]/8 text-[var(--sv-primary)] border-[#4B4DF7]/20 hover:bg-[#4B4DF7]/8 font-medium text-xs tracking-wide uppercase px-3 py-1"
          >
            La Soluzione
          </Badge>
          <h2
            data-testid="solution-title"
            className="font-['Inter_Tight'] text-3xl md:text-4xl font-bold tracking-tight text-[var(--sv-secondary)]"
          >
            Assessment AI con Skillvue
          </h2>
          <p className="mt-3 text-base text-slate-500 max-w-2xl">
            Skillvue ha abilitato un assessment strutturato, basato sul framework di soft skill che In's aveva già costruito internamente per il profilo di Store Manager.
          </p>
        </motion.div>

        {/* Soft Skills Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mb-14"
        >
          <h3 className="font-['Inter_Tight'] text-lg font-semibold text-[var(--sv-secondary)] mb-6 flex items-center gap-2">
            <Brain className="w-5 h-5 text-[var(--sv-primary)]" />
            Le 6 soft skill valutate
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {SOFT_SKILLS.map((skill, i) => (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, y: 12 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.35, delay: 0.2 + i * 0.08 }}
              >
                <div
                  data-testid={`skill-card-${i}`}
                  className="relative bg-gradient-to-br from-white to-slate-50 rounded-2xl p-6 border border-slate-100 shadow-[0_4px_20px_-4px_rgba(75,77,247,0.1)]"
                >
                  {/* Decorative gradient accent */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-[#4B4DF7]/5 to-transparent rounded-tr-2xl rounded-bl-full" />
                  
                  <div className="relative flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl ${skill.color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <skill.icon className="w-7 h-7" />
                    </div>
                    <p className="text-sm font-bold text-[var(--sv-secondary)] leading-snug">
                      {skill.name}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Key solution highlights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-14"
        >
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="p-6 border-slate-100 bg-[var(--sv-surface)]" data-testid="solution-highlight-0">
              <h4 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)] mb-3">
                Assessment da mobile
              </h4>
              <p className="text-sm text-slate-500 leading-relaxed">
                ~30 minuti da cellulare, svolto durante l'orario di lavoro come attività retribuita. 1.000 persone coinvolte su 570 punti vendita.
              </p>
            </Card>
            <Card className="p-6 border-slate-100 bg-[var(--sv-surface)]" data-testid="solution-highlight-1">
              <h4 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)] mb-3">
                Base neutra
              </h4>
              <p className="text-sm text-slate-500 leading-relaxed">
                Popolazione target scelta senza segnalazioni pregresse di alto potenziale: addetti vendita con 18-48 mesi di anzianità. Una fotografia oggettiva del talento reale, non una conferma delle impressioni esistenti.
              </p>
            </Card>
            <Card className="p-6 border-slate-100 bg-[var(--sv-surface)]" data-testid="solution-highlight-2">
              <h4 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)] mb-3">
                Comunicazione come leva di engagement
              </h4>
              <p className="text-sm text-slate-500 leading-relaxed">
                Attività retribuita, svolta in orario di lavoro e posizionata come investimento sullo sviluppo professionale. Non un esame, ma un'opportunità con un impatto diretto sulla partecipazione e sulla percezione positiva dell'intero progetto.
              </p>
            </Card>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
